import { useState, useContext, useEffect } from "react";
import { Stack, Box, Cluster } from "../../shared/layouts";
import Card from "../../shared/Card";
import Header from "../../shared/Header";
import Button from "../../shared/Button";
import axios from "axios";
import PreventUnload from "../../shared/PreventUnload";
import Input from "../../shared/Input";
import Labeled from "../../shared/WithLabel";
import PasswordCheckBox from "../../shared/PasswordCheckBox";
import useForm from "../../../hooks/useForm";
import { useNavigate } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import MessagebarContext from "../../../context/MessagebarContext";
import Loader from "../../shared/Loader";
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";

const EditPassword = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const [data, handleData] = useForm({});
  const [errors, setErrors] = useState({});
  const { SettingsIcon } = useIconLibrary();

  const navigate = useNavigate();
  const { setChangedDataWithoutSaving } = useContext(UserContext);
  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const [checks, setChecks] = useState({
    hasUppercase: false,
    hasNumber: false,
    hasSymbol: false,
    hasEightCharacters: false,
  });

  useEffect(() => {
    setChangedDataWithoutSaving(false);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setIsPasswordValid(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*\W).{8,}$/g.test(data.password))
  }, [data.password])

  const handleOnChange = (e) => {
    const { value } = e.target;

    const checks = {
      hasUppercase: /[A-Z]/.test(value),
      hasNumber: /\d/.test(value),
      hasSymbol: /\W/.test(value),
      hasEightCharacters: /^.{8,}$/.test(value),
    };

    setChecks(checks);
    handleData(e);
  };

  const save = (e) => {
    e.preventDefault();
    setErrors({});

    setIsLoading(true);
    axios
      .post("/user/settings/v2/password/change", data)
      .then(() => {
        setSuccesMessage();
        setErrors({});
        setChangedDataWithoutSaving(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setErrors(() => err.response.data.errors);
        setAlertMessage();
        setIsLoading(false);
      });
  };

  return (
    <PreventUnload>
      {isLoading && <Loader />}
      <Stack
        as="form"
        onSubmit={save}
        space="xl"
        width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
      >
        <Header
          title={t("Instellingen")}
          icon={<SettingsIcon width="2rem" height="2rem" />}
          variant="page"
        />
        <Card>
          <Stack space="m" width="100%">
            <Header title={t("Naam wijzigen")} variant="section" />
            <Cluster flex={1}>
              <Labeled label={t("Huidige wachtwoord")}>
                <Input
                  type="password"
                  name="old_password"
                  value={data.old_password}
                  onChange={handleData}
                  errors={errors.old_password}
                />
              </Labeled>
              <Box />
            </Cluster>
            <Cluster flex={1}>
              <Labeled label={t("Nieuwe wachtwoord")}>
                <Input
                  type="password"
                  name="password"
                  value={data.password}
                  onChange={handleOnChange}
                  errors={errors.password}
                />
              </Labeled>
              <Box />
            </Cluster>
            <Box>
              <PasswordCheckBox value={checks.hasUppercase}>
                {'1 ' + t('Hoofdletter')}
              </PasswordCheckBox>
              <PasswordCheckBox value={checks.hasNumber}>
                {'1 ' + t('Cijfer')}
              </PasswordCheckBox>
              <PasswordCheckBox value={checks.hasSymbol}>
                {'1 ' + t('Symbool')}
              </PasswordCheckBox>
              <PasswordCheckBox value={checks.hasEightCharacters}>
                {'8 ' + t('Karakters')}
              </PasswordCheckBox>
            </Box>
            <Cluster flex={1}>
              <Labeled label={t("Herhaal nieuwe wachtwoord")}>
                <Input
                  type="password"
                  name="password_confirmation"
                  value={data.password_confirmation}
                  onChange={handleData}
                  errors={errors.password_confirmation}
                />
              </Labeled>
              <Box />
            </Cluster>
          </Stack>
          <Cluster flex={1} mt="xl" maxWidth="100%">
            <Box fontScale="s" flex="50% 0 1">
              {t('Jouw wachtwoord wordt alleen gebruikt om in te loggen.')}
            </Box>
            <Cluster display="flex" justifyContent="flex-end" hGap="m">
              <Button
                variant="primary-outline"
                onClick={() => {
                  navigate("/user/settings");
                }}
              >
                {t('Annuleren')}
              </Button>
              <Button disabled={!isPasswordValid} variant="primary" type="submit">
                {t('Opslaan')}
              </Button>
            </Cluster>
          </Cluster>
        </Card>
      </Stack>
    </PreventUnload>
  );
};

export default EditPassword;
